import {
  forwardRef,
  Ref,
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box } from "@mui/material";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import isString from "lodash/isString";
import CheckboxTree from "components/shared/CheckboxTree";
import {
  flattenOptions,
  getNewSelections,
  ICheckboxOption,
  SELECT_ALL_CHECKBOX_OPTION,
} from "components/shared/CheckboxTree/CheckboxTree.utils";
import TextField from "components/shared/TextField";
import { Keyboard } from "utils/constants/keyboard.constants";
import { getIsEmptyString } from "utils/helpers";
import { flatMap } from "utils/helpers/flatMap";
import Loading from "../Loading";
import SelectionChips from "../SelectionChips/SelectionChips";
import {
  TypeaheadChipDisplayMode,
  TypeaheadFilterVariant,
} from "./MultiSelectTypeahead.constants";
import { TMultiSelectTypeaheadProps } from "./MultiSelectTypeahead.types";
import {
  getFilteredOptions,
  getOptionLabel,
} from "./MultiSelectTypeahead.utils";
import styles from "./MultiSelectTypeahead.styles";

const MultiSelectTypeahead = forwardRef(
  <T extends unknown>(
    multiSelectTypeaheadProps: TMultiSelectTypeaheadProps<T>,
    _ref: Ref<any>,
  ) => {
    const rootRef = useRef(null);
    const [inputValue, setInputValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const { t } = useTranslation("common");

    // omitting custom `onChange` & `textFieldProps` props so that it doesn't conflict with the existing `onChange` prop
    const {
      id,
      error,
      helperText,
      label,
      onChange,
      options,
      required,
      textFieldProps,
      value = [] as T[],
      noOptionsText = "",
      placeholder,
      openText = "",
      closeText = "",
      selectAllLabel = "",
      expandButtonLabel = "",
      onBlur,
      isSelectAllEnabled = false,
      getValue = (option: ICheckboxOption | undefined) => option?.id as T,
      testId,
      onOpen,
      onClose,
      variant = TypeaheadFilterVariant.Chips,
      renderListHeader,
      slotProps,
      disableClearable,
      chipDisplayMode = TypeaheadChipDisplayMode.All,
      hideTagsOnOpen = false,
      hideTagsOnClose = false,
      filterOptions = getFilteredOptions,
      disabled = false,
      loading,
      loadingText,
      open,
    } = multiSelectTypeaheadProps;

    const [isOpen, setIsOpen] = useState(false);

    const [isRenderedTagsHidden, setIsRenderedTagsHidden] = useState(
      (hideTagsOnOpen && hideTagsOnClose) || false,
    );

    const isDropdownOpen = open ?? isOpen;
    const shouldShowLoading = loading && isDropdownOpen;

    const parsedValues = useMemo(() => {
      return flattenOptions(options).filter((option) => {
        const optionValue = getValue(option);

        const optionIndex = value.findIndex((selectedOption) =>
          isEqual(optionValue, selectedOption),
        );

        return optionIndex > -1;
      });
    }, [value, getValue, options]);

    const handleSelectionChange = useCallback(
      (option: ICheckboxOption) => {
        if (onChange && value) {
          const newSelections = getNewSelections(parsedValues, option, options);

          onChange(newSelections.map((selection) => getValue(selection)));
        }
      },
      [onChange, value, parsedValues, options, getValue],
    );

    const handleAutocompleteOnChange = useCallback(
      (event: SyntheticEvent<Element, Event>, value: ICheckboxOption[]) => {
        return onChange?.(
          value.map((selection) =>
            isString(selection) ? selection : getValue(selection),
          ) as T[],
        );
      },
      [onChange, getValue],
    );

    const selectionChips = useMemo(() => {
      return flatMap(options).filter(
        (option) => value.indexOf(getValue(option)) > -1,
      );
    }, [value, options, getValue]);

    const sxProp = {
      ...styles.autocomplete,
      ...(multiSelectTypeaheadProps.sx || {}),
    };

    const isChipsVariant = variant === TypeaheadFilterVariant.Chips;
    const isChipRemovable = chipDisplayMode === TypeaheadChipDisplayMode.All;

    return (
      <Autocomplete
        data-qaid={testId}
        multiple
        id={id}
        noOptionsText={noOptionsText}
        slotProps={slotProps}
        limitTags={20}
        openText={openText}
        closeText={closeText}
        sx={sxProp}
        options={
          isSelectAllEnabled && !isEmpty(options)
            ? [
                {
                  id: SELECT_ALL_CHECKBOX_OPTION,
                  label: "",
                  value: "",
                  children: options,
                },
              ]
            : options
        }
        ref={rootRef}
        disabled={disabled}
        open={open}
        loading={shouldShowLoading}
        loadingText={loadingText}
        disableCloseOnSelect
        disableClearable={disableClearable}
        value={value}
        isOptionEqualToValue={(option, value) =>
          isEqual(getValue(option), value) || !option.value
        }
        onChange={handleAutocompleteOnChange}
        onOpen={(event) => {
          setIsOpen(true);

          if (hideTagsOnOpen) {
            setIsRenderedTagsHidden(true);
          }

          onOpen?.(event);
        }}
        onClose={() => {
          setIsOpen(false);
          setInputValue("");

          if (hideTagsOnOpen && !hideTagsOnClose) {
            setIsRenderedTagsHidden(false);
          }

          onClose?.();
        }}
        inputValue={inputValue}
        onInputChange={(_, value, reason) => {
          if (reason === "input") {
            setInputValue(value);
          }
        }}
        getOptionLabel={getOptionLabel}
        renderValue={() => {
          if (isRenderedTagsHidden) {
            return null;
          }

          return isChipsVariant ? (
            <SelectionChips
              chips={selectionChips}
              isRemovable={isChipRemovable}
              onChange={handleSelectionChange}
            />
          ) : (
            <Box
              sx={styles.selectedText}
              key={`${value.length} ${t("selected")}`}
            >
              {value.length} {t("selected")}
            </Box>
          );
        }}
        filterOptions={filterOptions}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          return (
            <React.Fragment key={key}>
              {renderListHeader}
              <CheckboxTree<T>
                options={[option]}
                optionProps={optionProps}
                onChange={onChange}
                values={parsedValues}
                isSelectAllEnabled={isSelectAllEnabled}
                selectAllSectionsLabel={selectAllLabel}
                expandButtonLabel={expandButtonLabel}
                isAllOptionsExpanded={!getIsEmptyString(inputValue)}
                getValue={getValue}
              />
            </React.Fragment>
          );
        }}
        renderInput={({
          InputProps: { startAdornment, ...restInputProps },
          ...restParams
        }) => {
          return (
            <TextField
              {...restParams}
              error={error}
              required={required}
              size="small"
              helperText={helperText}
              testId={testId}
              onBlur={() => {
                onBlur?.();
                setIsFocused(false);
              }}
              onFocus={() => setIsFocused(true)}
              onKeyDown={(event) => {
                if (!isChipRemovable && event.key === Keyboard.Backspace) {
                  event.stopPropagation();
                }
              }}
              label={label}
              placeholder={placeholder}
              {...textFieldProps}
              slotProps={{
                input: {
                  ...restInputProps,
                  ...(testId && { "data-qaid": `${testId}-input` }),
                  startAdornment: (
                    <Box sx={styles.startAdornment}>{startAdornment}</Box>
                  ),
                  endAdornment: (
                    <>
                      {shouldShowLoading ? <Loading size={20} /> : null}
                      {restInputProps.endAdornment}
                    </>
                  ),
                },
                inputLabel: {
                  shrink:
                    isFocused ||
                    !getIsEmptyString(inputValue) ||
                    value.length > 0,
                },
              }}
            />
          );
        }}
      />
    );
  },
);

export default MultiSelectTypeahead as <T>(
  multiSelectTypeaheadProps: TMultiSelectTypeaheadProps<T>,
  ref?: Ref<any>,
) => ReturnType<typeof MultiSelectTypeahead>;
